import { useCallback, useEffect, useRef, useState } from 'react';
import { detectPitch, frequencyToMidi } from '../lib/pitch';

export type MicrophoneStatus = 'idle' | 'requesting' | 'active' | 'denied' | 'unavailable' | 'error';

export function useMicrophone() {
  const [status, setStatus] = useState<MicrophoneStatus>('idle');
  const [frequency, setFrequency] = useState<number | null>(null);
  const [level, setLevel] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const frameRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);

  const stop = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    setFrequency(null);
    setLevel(0);
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable');
      return;
    }
    setStatus('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
      });
      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.15;
      source.connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);
      streamRef.current = stream;
      contextRef.current = context;
      setStatus('active');
      const tick = () => {
        analyser.getFloatTimeDomainData(buffer);
        let peak = 0;
        for (const value of buffer) peak = Math.max(peak, Math.abs(value));
        setLevel(Math.min(1, peak * 4));
        setFrequency(detectPitch(buffer, context.sampleRate));
        frameRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (error) {
      setStatus(error instanceof DOMException && error.name === 'NotAllowedError' ? 'denied' : 'error');
    }
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || typeof MediaRecorder === 'undefined') return false;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined });
    recorder.ondataavailable = (event) => event.data.size && chunksRef.current.push(event.data);
    recorder.start(500);
    recorderRef.current = recorder;
    setRecording(true);
    return true;
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise<Blob | null>((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') return resolve(null);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        recorderRef.current = null;
        setRecording(false);
        resolve(blob);
      };
      recorder.stop();
    });
  }, []);

  useEffect(() => stop, [stop]);

  return {
    status,
    frequency,
    midi: frequency ? frequencyToMidi(frequency) : null,
    level,
    stream: streamRef.current,
    recording,
    start,
    stop,
    startRecording,
    stopRecording,
  };
}
